import { useRef, useEffect } from 'react';
import { ArrowLeftIcon, HeartIcon, ShareIcon } from '@heroicons/react/24/outline';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from './Footer';

interface Product {
  id: number;
  name: string;
  price: string;
  description: string;
  details: string[];
  image: string;
  video: string;
  videoMp4: string;
  slug: string;
}

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Product data - in real app, this would come from API
  const products: Product[] = [
    {
      id: 1,
      name: 'Hermes Mini Kelly',
      price: 'NRS xxx',
      description: 'Black • Premium Leather • Limited Edition',
      details: [
        'Handcrafted premium leather construction',
        'Signature Kelly lock closure',
        'Adjustable shoulder strap',
        'Interior zip pocket',
        'Dust bag included',
        'Authenticity certificate'
      ],
      image: '/hermes.png',
      video: '/hermes.webm',
      videoMp4: '/hermes.mp4',
      slug: 'hermes-mini-kelly'
    },
    {
      id: 2,
      name: 'White Coach',
      price: 'NRS xxx',
      description: 'White • Signature Leather • Classic',
      details: [
        'Signature Coach leather',
        'Classic design with modern touches',
        'Multiple interior compartments',
        'Detachable strap',
        'Coach authenticity card',
        'Care instructions included'
      ],
      image: '/whitecoach.png',
      video: '/whitecoach.webm',
      videoMp4: '/whitecoach.mp4',
      slug: 'white-coach'
    }
  ];

  const product = products.find(p => p.slug === slug);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        console.log('Video autoplay failed');
      });
    }
  }, [slug]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <button 
            onClick={() => navigate('/')}
            className="luxury-gradient text-white px-6 py-3 rounded-full font-medium"
          >
            Back to Collection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Transparent Header - Same as Homepage */}
      <header className="fixed w-full top-0 z-50 bg-transparent">
        <nav className="px-4">
          <div className="flex h-14 items-center justify-between">
            {/* Back Button */}
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white hover:text-gold-300 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>

            {/* Center Logo */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <h1 className="text-lg font-serif font-bold tracking-[0.2em] text-white">
                ELARA
              </h1>
            </div>

            {/* Right Icons */}
            <div className="flex items-center space-x-4">
              <button className="text-white hover:text-gold-300 transition-colors">
                <HeartIcon className="h-5 w-5" />
              </button>
              <button className="text-white hover:text-gold-300 transition-colors">
                <ShareIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Full-Screen Product Video - Same as Homepage */}
      <section className="relative h-screen w-full overflow-hidden bg-black">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            loop
            muted
            playsInline
            autoPlay
          >
            <source src={product.video} type="video/webm" />
            <source src={product.videoMp4} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Video Overlay */}
          <div className="absolute inset-0 bg-black/10"></div>
        </div>

        {/* Product Name Overlay - Like Homepage ELARA */}
        <div className="relative z-10 h-full flex items-center justify-center pt-20">
          <div className="text-center text-white">
            <h1 className="text-4xl font-serif font-bold tracking-[0.2em] mb-2">
              {product.name.toUpperCase()}
            </h1>
            <p className="text-lg font-light opacity-90">
              {product.description}
            </p>
          </div>
        </div>
      </section>

      {/* Product Details Section - Clean & Minimal */}
      <section className="bg-white py-16">
        <div className="px-6 text-center">
          <div className="max-w-sm mx-auto">
            {/* Price - Clean Typography */}
            <div className="mb-8">
              <span className="text-2xl font-light text-gray-900 tracking-wide">
                {product.price}
              </span>
            </div>

            {/* Add to Cart - Minimal Design */}
            <button className="w-full bg-black text-white py-3 px-8 font-medium tracking-wide uppercase text-sm border-2 border-black hover:bg-white hover:text-black transition-all duration-300">
              Add to Cart
            </button>

            {/* Quick Info - Subtle */}
            <div className="mt-6 text-xs text-gray-500 space-y-2 font-light">
              <p>Authentic & Premium Quality</p>
              <p>Secure Delivery & Easy Returns</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProductDetail;